import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Shield } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const PrivacyPolicyPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gray-900">
      <SEOHead 
        title="Privacy Policy - AI BookMoney"
        description="Privacy Policy for AI BookMoney. Learn how we collect, use, and protect your personal information when using our AI publishing platform."
        keywords="privacy policy, data protection, AI BookMoney privacy, user data, GDPR compliance"
        canonical="/privacy"
        ogTitle="Privacy Policy - AI BookMoney"
        ogDescription="Privacy Policy for AI BookMoney AI-powered publishing platform"
        ogImage="/ai-manuscript-demo.png"
        ogUrl="/privacy"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Privacy Policy",
          "description": "Privacy Policy for AI BookMoney platform",
          "url": "https://aibookmoney.com/privacy"
        }}
      />
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3 mb-4">
            <BookOpen className="h-8 w-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">AI BookMoney</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-green-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
              <p className="text-gray-400 mt-1">Last updated: January 19, 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-invert max-w-none">
          
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-green-300 mb-3">Your Privacy Matters</h2>
            <p className="text-green-200">
              JMFT LLC ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, and safeguard your information when you use AI BookMoney.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
          
          <h3 className="text-xl font-semibold text-white mb-3">1.1 Personal Information</h3>
          <p className="text-gray-300 mb-4">When you create an account, we collect:</p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Name and email address</li>
            <li>Password (encrypted and securely stored)</li>
            <li>Account preferences and settings</li>
          </ul>

          <h3 className="text-xl font-semibold text-white mb-3">1.2 Usage Information</h3>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Content you create using our AI tools</li>
            <li>Feature usage and interaction patterns</li>
            <li>Credit consumption and subscription status</li>
            <li>Support inquiries and communications</li>
          </ul>

          <h3 className="text-xl font-semibold text-white mb-3">1.3 Technical Information</h3>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>IP address and browser information</li>
            <li>Device type and operating system</li>
            <li>Log data and error reports</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
          <p className="text-gray-300 mb-4">We use collected information to:</p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Provide and improve our AI-powered services</li>
            <li>Process your content generation requests</li>
            <li>Manage your account and subscription</li>
            <li>Send important service-related notifications</li>
            <li>Provide customer support</li>
            <li>Detect and prevent fraud or abuse</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">3. AI Content Processing</h2>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-purple-300 mb-3">Important Note About AI Processing</h3>
            <ul className="list-disc list-inside text-purple-200 space-y-2">
              <li>Your prompts and generated content are processed by our AI systems</li>
              <li>We may use anonymized data to improve our AI models</li>
              <li>Your personal content is not shared with third parties for marketing</li>
              <li>Generated content belongs to you once created</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">4. Information Sharing</h2>
          <p className="text-gray-300 mb-4">We do not sell your personal information. We may share information with:</p>
          
          <h3 className="text-xl font-semibold text-white mb-3">4.1 Service Providers</h3>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>Payment processors (Stripe) for billing</li>
            <li>Email service providers (Postmark) for notifications</li>
            <li>Cloud hosting providers for infrastructure</li>
            <li>AI service providers for content generation</li>
          </ul>

          <h3 className="text-xl font-semibold text-white mb-3">4.2 Legal Requirements</h3>
          <p className="text-gray-300 mb-6">
            We may disclose information when required by law, court order, or to protect our rights and safety.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
          <p className="text-gray-300 mb-4">We implement robust security measures including:</p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Encryption of sensitive data in transit and at rest</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and monitoring</li>
            <li>SOC 2 compliant hosting infrastructure</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">6. Cookies and Tracking</h2>
          <p className="text-gray-300 mb-4">We use cookies for:</p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Authentication and session management</li>
            <li>User preferences and settings</li>
            <li>Analytics to improve our service</li>
            <li>Security and fraud prevention</li>
          </ul>
          <p className="text-gray-300 mb-6">
            You can control cookies through your browser settings, but some features may not work properly if disabled.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">7. Data Retention</h2>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Account data: Retained while your account is active</li>
            <li>Generated content: Stored for your access and backup purposes</li>
            <li>Usage logs: Retained for 12 months for security and improvement</li>
            <li>Deleted accounts: Data permanently deleted within 30 days</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">8. Your Rights</h2>
          <p className="text-gray-300 mb-4">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Delete your account and associated data</li>
            <li>Export your generated content</li>
            <li>Object to certain data processing activities</li>
            <li>Withdraw consent where applicable</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
          <p className="text-gray-300 mb-6">
            AI BookMoney is not intended for children under 13. We do not knowingly collect personal information 
            from children under 13. If we become aware of such collection, we will take steps to delete the information.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">10. International Data Transfers</h2>
          <p className="text-gray-300 mb-6">
            Your data may be processed in the United States and other countries where our service providers operate. 
            We ensure appropriate safeguards are in place for international transfers.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">11. Changes to Privacy Policy</h2>
          <p className="text-gray-300 mb-6">
            We may update this Privacy Policy periodically. We will notify you of significant changes via email 
            or through our service. Continued use after changes constitutes acceptance.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <p className="text-gray-300 mb-4">
              For privacy-related questions or to exercise your rights, contact us:
            </p>
            <div className="space-y-2">
              <p className="text-gray-300"><strong className="text-white">JMFT LLC</strong></p>
              <p className="text-gray-300">Privacy Officer</p>
              <p className="text-gray-300">Email: privacy@aibookmoney.com</p>
              <p className="text-gray-300">Support: support@aibookmoney.com</p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <p className="text-gray-400 text-sm">
              This Privacy Policy is governed by the laws of the United States and is compliant with applicable 
              privacy regulations including CCPA and GDPR where applicable.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;